import { Bell, ChevronRight, X } from 'lucide-react';

type PushNotificationToastProps = {
  title?: string;
  body?: string;
  subtitle?: string;
  onClick?: () => void;
  closeToast?: () => void;
};

export function PushNotificationToast({
  title,
  body,
  subtitle,
  onClick,
  closeToast,
}: PushNotificationToastProps) {
  const isClickable = !!onClick;

  const handleClick = () => {
    if (!onClick) return;
    onClick();
    closeToast?.();
  };

  return (
    <div
      role={isClickable ? 'button' : 'status'}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`group relative flex w-full items-start gap-3 rounded-[10px] border-l-4 border-[#FEC733] bg-white p-3.5 pr-9 text-black shadow-[0px_4px_12px_0px_#00000026] dark:bg-darkPanelBg dark:text-white dark:shadow-[0px_4px_12px_0px_#00000080] ${
        isClickable ? 'cursor-pointer hover:bg-[#FFF8E1] dark:hover:bg-[#0A3764]' : ''
      }`}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FEC733]/20 text-[#E0A800] dark:bg-[#FEC733]/15 dark:text-[#FEC733]">
        <Bell size={20} />
      </span>

      <div className="min-w-0 flex-1">
        {title && <p className="truncate text-[15px] font-semibold leading-5">{title}</p>}
        {body && (
          <p className="mt-0.5 text-[13px] leading-[18px] text-black/70 dark:text-white/80">
            {body}
          </p>
        )}
        {subtitle && (
          <p className="mt-1 truncate text-xs leading-4 text-black/50 dark:text-white/50">
            {subtitle}
          </p>
        )}
        {isClickable && (
          <span className="mt-1.5 inline-flex items-center gap-0.5 text-xs font-semibold text-[#E0A800] dark:text-[#FEC733]">
            View details
            <ChevronRight size={13} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        )}
      </div>

      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          closeToast?.();
        }}
        className="absolute right-2 top-2 rounded p-0.5 text-black/40 hover:bg-black/5 hover:text-black dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white"
      >
        <X size={16} />
      </button>
    </div>
  );
}
